/* Diamond Studio — product page CRO helpers (2026-09-29)
   1. Carat weight: the same design in its other sizes, as one segmented row with live prices
      (sizes come from assets/ds-size-families.js, built from the product handles).
   2. Trust line under the price: IGI report · free insured shipping · arrival dates · 60-day returns.
   3. Gold tiles: metal swatch, name and cost ("Included" / "+$500"), all the same height.
   4. Engraving starts folded into one "Add engraving" row, so the buttons sit higher.
   5. Free diamond studs card says whether this piece (plus the bag) qualifies, or how far away it is.
   6. Phones: the bottom purchase bar steps aside while the main buttons are on screen.
   7. "Drop a hint": share this piece by text, email, WhatsApp or link.
   8. "Recently viewed" row near the bottom of the page. */
(() => {
  'use strict';
  if (window.DSPdpCRO) return;
  window.DSPdpCRO = true;
  const body = document.body;
  if (!body || !body.classList.contains('signature-product-page')) return;

  const PHONE = window.matchMedia ? window.matchMedia('(max-width: 700px)') : null;
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const track = (event, extra) => { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({event}, extra || {})); };
  const config = () => window.DS_PREVIEW_CONFIG || {};
  const currency = () => config().priceCurrency || config().currency || window.DS_CRO?.currency || 'USD';
  const money = (cents, cur) => {
    try {
      return new Intl.NumberFormat('en-US', {style: 'currency', currency: cur || currency(), minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2}).format(cents / 100);
    } catch (e) { return '$' + Math.round(cents / 100).toLocaleString('en-US'); }
  };
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const handle = () => {
    const fromConfig = config().productData?.handle;
    if (fromConfig) return String(fromConfig);
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };
  function waitFor(test, done, timeoutMs) {
    const start = Date.now();
    (function tick() {
      let result = null;
      try { result = test(); } catch (e) { result = null; }
      if (result) { done(result); return; }
      if (Date.now() - start > (timeoutMs || 15000)) return;
      setTimeout(tick, 200);
    })();
  }
  const productCache = {};
  const fetchProduct = h => productCache[h] || (productCache[h] = fetch(root() + 'products/' + encodeURIComponent(h) + '.js', {credentials: 'same-origin'})
    .then(r => r.ok ? r.json() : null).catch(() => null));

  const icons = {
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>',
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>',
    calendar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="17" height="15" rx="1.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>',
    returns: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 9h11a5 5 0 010 10H9"/><path d="M8 5L4 9l4 4"/></svg>',
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3.5" y="8" width="17" height="4" rx="1"/><path d="M5 12v8h14v-8M12 8v12M12 8c-1.5-3-5-3.5-5-1.2C7 8 9 8 12 8zm0 0c1.5-3 5-3.5 5-1.2C17 8 15 8 12 8z"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    hint: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7"/><path d="M12 15V3M7.5 7.5L12 3l4.5 4.5"/></svg>'
  };

  /* ---------- Arrival dates (same promise as the collection cards: 2–2½ weeks) ---------- */
  const arrival = (() => {
    const fmt = new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric'});
    const add = n => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
    const a = add(14), b = add(18);
    return a.getMonth() === b.getMonth() ? fmt.format(a) + '–' + b.getDate() : fmt.format(a) + ' – ' + fmt.format(b);
  })();

  /* ---------- 1. Carat weight switcher ---------- */
  const CARAT = /(^|-)(\d+)(?:-(\d{1,2}))?-(carat|ct)(?=-|$)/;
  async function sizeSwitcher() {
    const url = window.DS_PDP_FAMILIES_URL;
    const current = handle();
    const m = current.match(CARAT);
    if (!url || !m) return;
    const key = current.slice(0, m.index + m[1].length) + '#' + current.slice(m.index + m[0].length);
    // Rows are packed: see the note at the top of assets/ds-size-families.js.
    const packed = key.replace(/lab-grown-diamond/g, '~L').replace(/engagement-ring/g, '~E').replace(/lab-grown/g, '~G');
    // Unnamed families (necklaces, bracelets, earrings, bands) can share a name without being one design; rings only.
    if (packed.startsWith('#-') && !/~E|bridal-set/.test(packed)) return;
    if (!window.DS_SIZE_FAMILIES) {
      await new Promise(resolve => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = resolve;
        script.onerror = resolve;
        document.head.appendChild(script);
      });
    }
    const row = String(window.DS_SIZE_FAMILIES || '').split('|').find(r => r.startsWith(packed + ' '));
    if (!row) return;
    const tokens = row.slice(packed.length + 1).split(' ').filter(Boolean).map(t => /-ct$/.test(t) ? t : t + '-carat');
    if (tokens.length < 2) return;
    const members = tokens.map(t => {
      const tm = t.match(/^(\d+)(?:-(\d{1,2}))?-(carat|ct)$/);
      return tm ? {h: key.replace('#', t), ct: parseFloat(tm[1] + '.' + (tm[2] || '0')), label: tm[1] + (tm[2] ? '.' + tm[2] : '') + ' ct'} : null;
    }).filter(Boolean);
    if (!members.some(x => x.h === current)) return;
    const products = await Promise.all(members.map(x => fetchProduct(x.h)));
    const rows = members.map((x, i) => Object.assign(x, {p: products[i]})).filter(x => x.p && (x.p.available || x.h === current));
    if (rows.length < 2) return;

    waitFor(() => document.querySelector('#configure .ds-summary-facts') || document.querySelector('#configure section.a-metal'), anchor => {
      if (document.getElementById('ds-pc-sizes')) return;
      const giftOn = currency() === 'USD';
      const threshold = window.DSStudGiftConfig?.threshold || 300000;
      const box = document.createElement('section');
      box.id = 'ds-pc-sizes';
      box.className = 'ds-pc-sizes';
      box.setAttribute('aria-labelledby', 'ds-pc-sizes-title');
      // Segmented control: one row, a diamond that grows with the carat weight, the price underneath.
      const cts = rows.map(x => x.ct);
      const lo = Math.min(...cts), hi = Math.max(...cts);
      const gem = ct => {
        const size = Math.round(12 + (hi > lo ? (ct - lo) / (hi - lo) : 0.5) * 12);
        return '<svg width="' + size + '" height="' + size + '" style="width:' + size + 'px;height:' + size + 'px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7 4h10l4 5-9 11L3 9z"/><path d="M3 9h18M10 4L8 9l4 11 4-11-2-5"/></svg>';
      };
      const qualifies = x => giftOn && x.p.price >= threshold;
      const currentRow = rows.find(x => x.h === current);
      const giftRows = rows.filter(qualifies);
      let note = '';
      if (giftRows.length && currentRow && !qualifies(currentRow)) {
        const first = giftRows[0];
        const allAbove = rows.filter(x => x.ct >= first.ct).every(qualifies);
        const text = giftRows.length === 1 ? first.label + ' qualifies for free diamond studs'
          : allAbove ? first.label + ' and up qualify for free diamond studs'
          : giftRows.map(x => x.label).join(', ') + ' qualify for free diamond studs';
        note = '<p class="ds-pc-sizes__note">' + icons.gift + '<span>' + esc(text) + '</span></p>';
      }
      box.innerHTML = '<div class="ds-pc-sizes__head"><h2 id="ds-pc-sizes-title">Carat weight</h2><span>Same design · ' + rows.length + ' sizes</span></div>' +
        '<ul class="ds-pc-seg" role="list" style="--ds-pc-n:' + rows.length + '">' + rows.map(x => {
          const gift = qualifies(x) ? '<span class="ds-pc-seg__gift" title="Qualifies for free diamond studs">' + icons.gift + '</span>' : '';
          const inner = '<span class="ds-pc-seg__gem">' + gem(x.ct) + '</span><strong>' + esc(x.label) + '</strong><span class="ds-pc-seg__price">' + esc(money(x.p.price)) + '</span>' + gift;
          const label = x.label + ', ' + money(x.p.price) + (qualifies(x) ? ', qualifies for free diamond studs' : '');
          return x.h === current
            ? '<li><span class="ds-pc-seg__opt is-current" aria-current="page" aria-label="' + esc(label + ', selected') + '">' + inner + '</span></li>'
            : '<li><a class="ds-pc-seg__opt" href="' + esc(x.p.url || (root() + 'products/' + x.h)) + '" data-ds-pc-size="' + esc(x.label) + '" aria-label="' + esc(label) + '">' + inner + '</a></li>';
        }).join('') + '</ul>' + note;
      // Keep the chosen gold (and the preview theme) when moving to another size.
      box.addEventListener('click', event => {
        const link = event.target.closest('a[data-ds-pc-size]');
        if (!link) return;
        try {
          const target = new URL(link.getAttribute('href'), location.origin);
          const gold = window.DS_APP_SELECTION?.gold || document.querySelector('#ds-options-mount input[name="image-swatches-1"]:checked')?.value;
          if (gold) target.searchParams.set('gold', gold);
          const preview = new URLSearchParams(location.search).get('preview_theme_id');
          if (preview) target.searchParams.set('preview_theme_id', preview);
          link.href = target.pathname + target.search;
        } catch (e) { /* follow the plain link */ }
        track('ds_pdp_size_switch', {size: link.dataset.dsPcSize});
      });
      if (anchor.classList.contains('ds-summary-facts')) anchor.after(box);
      else anchor.before(box);
    });
  }

  /* ---------- 2. Trust line under the price ---------- */
  function trustLine() {
    waitFor(() => document.querySelector('.a-price-row'), row => {
      if (row.querySelector('.ds-pc-assure')) return;
      const items = [];
      if (window.DS_PRODUCT_IGI_INCLUDED) items.push([icons.shield, 'IGI report included']);
      items.push([icons.truck, 'Free insured shipping']);
      items.push([icons.calendar, 'Arrives ' + arrival]);
      items.push([icons.returns, '60-day returns']);
      const list = document.createElement('ul');
      list.className = 'ds-pc-assure';
      list.setAttribute('role', 'list');
      list.setAttribute('aria-label', 'Included with this piece');
      list.innerHTML = items.map(([icon, text]) => '<li>' + icon + '<span>' + esc(text) + '</span></li>').join('');
      list.querySelector('li:nth-child(' + (items.length - 1) + ')').title = 'Every piece is made new for you. Estimated arrival about 2–2½ weeks from ordering (not a guarantee).';
      row.append(list);
    });
  }

  /* ---------- 3. Gold tiles: metal swatch, name and what it costs ---------- */
  // Every metal tile gets the same three parts (swatch, name, "Included" or "+$500"), so no tile
  // grows taller than the others. Non-metal swatches are left exactly as the app draws them.
  function upgradePrices() {
    waitFor(() => document.getElementById('ds-options-mount'), watchUpgrades);
  }
  const metalOf = value => /rose/i.test(value) ? 'rose' : /yellow/i.test(value) ? 'yellow' : /platinum/i.test(value) ? 'platinum' : /white/i.test(value) ? 'white' : '';
  function watchUpgrades(mount) {
    const apply = () => {
      const variants = config().addonProduct?.variants || [];
      mount.querySelectorAll('.gpo-swatches input').forEach(input => {
        const label = input.nextElementSibling;
        const value = String(input.value || '');
        const metal = metalOf(value);
        if (!label || label.tagName !== 'LABEL' || !metal) return;
        if (label.dataset.dsMetal !== metal) {
          label.dataset.dsMetal = metal;
          label.dataset.dsKarat = (value.match(/(\d{2})\s*K/i) || [])[1] || '';
          label.classList.add('ds-pc-metal-tile');
          label.closest('.gpo-ov-wrapper')?.classList.add('ds-pc-metal-grid');
        }
        if (label.querySelector('.ds-pc-upcharge')) return;
        const ids = (input.getAttribute('gpo-data-variant-id') || '').split(',').filter(Boolean);
        const cents = ids.reduce((sum, id) => {
          const v = variants.find(x => String(x.id) === id) || (window.GPOConfigs?.addonVariants || []).find(x => String(x.id) === id);
          const price = Number(v?.price);
          return sum + (Number.isFinite(price) ? price : 0);
        }, 0);
        const tag = document.createElement('span');
        tag.className = 'ds-pc-upcharge' + (cents > 0 ? '' : ' is-included');
        tag.textContent = cents > 0 ? '+' + money(cents) : 'Included';
        label.appendChild(tag);
        input.setAttribute('aria-label', value + (cents > 0 ? ', adds ' + money(cents) : ', included in the price'));
      });
    };
    apply();
    new MutationObserver(() => apply()).observe(mount, {childList: true, subtree: true});
  }

  /* ---------- 4. Engraving folded into one row ---------- */
  function foldEngraving() {
    const setup = section => {
      if (section.dataset.dsPcFold) return;
      section.dataset.dsPcFold = '1';
      const field = section.querySelector('#ds-engraving-text');
      if (field && field.value.trim()) return;
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'ds-pc-eng-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', 'ds-engraving-text');
      toggle.innerHTML = '<span class="ds-pc-eng-toggle__plus" aria-hidden="true">+</span><span>Add engraving</span><small>Optional · $10 per character</small>';
      const open = focus => {
        if (!section.classList.contains('ds-pc-eng-closed')) return;
        section.classList.remove('ds-pc-eng-closed');
        toggle.setAttribute('aria-expanded', 'true');
        if (focus) section.querySelector('#ds-engraving-text')?.focus();
      };
      toggle.addEventListener('click', () => { open(true); track('ds_pdp_engraving_open'); });
      section.prepend(toggle);
      section.classList.add('ds-pc-eng-closed');
      // Anything that needs the field (an error, a saved value) opens it first.
      document.addEventListener('ds:engraving', event => { if (event.detail?.text) open(false); });
      const api = window.DSEngraving;
      if (api && typeof api.focus === 'function' && !api.focus.dsPcWrapped) {
        const original = api.focus;
        api.focus = function () { open(false); return original.apply(this, arguments); };
        api.focus.dsPcWrapped = true;
      }
    };
    waitFor(() => document.getElementById('ds-engraving'), setup);
    // The engraving script rebuilds its section if the buy box is redrawn.
    new MutationObserver(() => { const s = document.getElementById('ds-engraving'); if (s && !s.dataset.dsPcFold) setup(s); })
      .observe(body, {childList: true, subtree: true});
  }

  /* ---------- 5. Free diamond studs: does this piece qualify? ---------- */
  let cart = null;
  const loadCart = () => fetch(root() + 'cart.js', {credentials: 'same-origin'}).then(r => r.ok ? r.json() : null).then(c => { cart = c; renderGift(); }).catch(() => {});
  function renderGift() {
    const card = document.getElementById('ds-stud-gift-product');
    if (!card) return;
    const gift = window.DSStudGiftConfig || {threshold: 300000, currency: 'USD', inclusive: true};
    let line = card.querySelector('.ds-pc-gift-status');
    const selection = window.DS_PRODUCT_SELECTION?.();
    const base = Number(selection?.priceCents);
    if (currency() !== gift.currency || !Number.isSafeInteger(base) || base <= 0) { if (line) line.hidden = true; return; }
    const item = base; // The selection price already includes a gold upgrade.
    const goal = gift.threshold + (gift.inclusive === false ? 1 : 0);
    const productId = String(config().productData?.id || selection?.productId || '');
    const cartOk = cart && cart.currency === gift.currency && Number.isSafeInteger(cart.total_price);
    const inBag = cartOk && cart.items.some(i => String(i.product_id) === productId);
    const bag = cartOk ? cart.total_price : 0;
    const total = inBag ? bag : bag + item;
    let text, ok = false;
    if (item >= goal) { text = 'This piece qualifies for free diamond studs.'; ok = true; }
    else if (total >= goal) { text = inBag ? 'Your bag qualifies for free diamond studs.' : 'With your bag, this order qualifies for free diamond studs.'; ok = true; }
    else if (bag > 0 && !inBag) text = 'With this piece and your bag, you’re ' + money(goal - total, gift.currency) + ' away.';
    else text = 'With this piece, you’re ' + money(goal - total, gift.currency) + ' away.';
    if (!line) {
      line = document.createElement('p');
      line.className = 'ds-pc-gift-status';
      line.setAttribute('role', 'status');
      const copy = card.querySelector('.ds-gift-copy');
      if (copy) copy.after(line); else card.append(line);
    }
    line.hidden = false;
    line.classList.toggle('is-qualified', ok);
    line.innerHTML = (ok ? icons.check : '') + '<span>' + esc(text) + '</span>';
  }
  function giftStatus() {
    waitFor(() => document.getElementById('ds-stud-gift-product') && window.DS_PRODUCT_SELECTION, () => {
      renderGift();
      loadCart();
      ['ds:app-options', 'ds:selection', 'ds:engraving'].forEach(name => document.addEventListener(name, () => setTimeout(renderGift, 0)));
      document.addEventListener('ds:cart-added', () => setTimeout(loadCart, 300));
      document.getElementById('configure')?.addEventListener('change', () => setTimeout(renderGift, 50));
    });
  }

  /* ---------- 6. Phones: bottom bar steps aside while the main buttons are visible ---------- */
  function stickyBar() {
    waitFor(() => document.getElementById('buy-actions') && document.querySelector('.a-sticky-buy'), () => {
      const actions = document.getElementById('buy-actions');
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(entries => {
        entries.forEach(entry => body.classList.toggle('ds-pc-buy-visible', entry.isIntersecting));
      }, {rootMargin: '0px 0px -40px 0px'}).observe(actions);
    });
  }

  /* ---------- 7. Drop a hint ---------- */
  function dropHint() {
    waitFor(() => document.querySelector('.a-product-label [data-pdp-action="save"]'), save => {
      if (document.querySelector('.ds-pc-hint')) return;
      const title = (document.querySelector('.ds-product-title')?.textContent || document.title).replace(/\s+/g, ' ').trim();
      const shareUrl = () => {
        const u = new URL(location.pathname.replace(/^\/collections\/[^/]+/, ''), location.origin);
        const gold = window.DS_APP_SELECTION?.gold;
        if (gold) u.searchParams.set('gold', gold);
        u.searchParams.set('utm_source', 'drop_a_hint');
        u.searchParams.set('utm_medium', 'share');
        return u.toString();
      };
      const message = 'I love this piece from Diamond Studio: ' + title;
      const wrap = document.createElement('div');
      wrap.className = 'ds-pc-hint';
      wrap.innerHTML = '<button type="button" class="ds-pc-hint__button" aria-haspopup="true" aria-expanded="false">' + icons.hint + '<span>Drop a hint</span></button>' +
        '<div class="ds-pc-hint__menu" role="menu" hidden>' +
          '<p class="ds-pc-hint__intro">Send this piece to someone who should know.</p>' +
          '<a role="menuitem" data-hint="email" href="#">Email</a>' +
          '<a role="menuitem" data-hint="sms" href="#">Text message</a>' +
          '<a role="menuitem" data-hint="whatsapp" href="#" target="_blank" rel="noopener">WhatsApp</a>' +
          '<button type="button" role="menuitem" data-hint="copy">Copy link</button>' +
        '</div>';
      const button = wrap.querySelector('.ds-pc-hint__button');
      const menu = wrap.querySelector('.ds-pc-hint__menu');
      const close = () => { menu.hidden = true; button.setAttribute('aria-expanded', 'false'); };
      const openMenu = () => {
        const url = shareUrl();
        menu.querySelector('[data-hint="email"]').href = 'mailto:?subject=' + encodeURIComponent('A little hint') + '&body=' + encodeURIComponent(message + '\n' + url);
        menu.querySelector('[data-hint="sms"]').href = 'sms:?&body=' + encodeURIComponent(message + ' ' + url);
        menu.querySelector('[data-hint="whatsapp"]').href = 'https://wa.me/?text=' + encodeURIComponent(message + ' ' + url);
        menu.querySelector('[data-hint="sms"]').hidden = !(PHONE && PHONE.matches);
        menu.hidden = false;
        button.setAttribute('aria-expanded', 'true');
      };
      button.addEventListener('click', async () => {
        track('ds_pdp_hint_open');
        if (!menu.hidden) { close(); return; }
        if (navigator.share && PHONE && PHONE.matches) {
          try { await navigator.share({title: 'A little hint', text: message, url: shareUrl()}); track('ds_pdp_hint_share', {method: 'native'}); return; }
          catch (e) { if (e && e.name === 'AbortError') return; }
        }
        openMenu();
      });
      menu.addEventListener('click', async event => {
        const item = event.target.closest('[data-hint]');
        if (!item) return;
        track('ds_pdp_hint_share', {method: item.dataset.hint});
        if (item.dataset.hint === 'copy') {
          event.preventDefault();
          try { await navigator.clipboard.writeText(shareUrl()); item.textContent = 'Link copied'; }
          catch (e) { item.textContent = 'Copy failed'; }
          setTimeout(() => { item.textContent = 'Copy link'; close(); }, 1600);
          return;
        }
        setTimeout(close, 100);
      });
      document.addEventListener('click', event => { if (!wrap.contains(event.target)) close(); });
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && !menu.hidden) { close(); button.focus(); } });
      save.before(wrap);
    });
  }

  /* ---------- 8. Recently viewed ---------- */
  async function recentlyViewed() {
    let list = [];
    try { list = JSON.parse(localStorage.getItem('ds-recently-viewed-v1') || '[]'); } catch (e) { return; }
    const current = handle();
    const handles = list.filter(i => i && i.h && i.h !== current && Date.now() - i.t < 1000 * 60 * 60 * 24 * 60).slice(0, 8).map(i => i.h);
    if (!handles.length) return;
    const items = (await Promise.all(handles.map(fetchProduct))).filter(p => p && p.available && p.featured_image);
    if (!items.length || document.querySelector('.ds-pc-recent')) return;
    const img = src => { const b = src.startsWith('//') ? 'https:' + src : src; return b + (b.includes('?') ? '&' : '?') + 'width=360'; };
    const section = document.createElement('section');
    section.className = 'ds-pc-recent';
    section.setAttribute('aria-labelledby', 'ds-pc-recent-title');
    section.innerHTML = '<div class="ds-pc-recent__inner"><p class="ds-pc-recent__eyebrow">Your shortlist</p><h2 class="ds-pc-recent__title" id="ds-pc-recent-title">Recently viewed</h2>' +
      '<ul class="ds-pc-recent__list" role="list">' + items.map(p =>
        '<li><a href="' + esc(p.url) + '" data-ds-pc-recent><img src="' + esc(img(p.featured_image)) + '" alt="" width="180" height="180" loading="lazy"><span class="ds-pc-recent__name">' + esc(p.title) + '</span><span class="ds-pc-recent__price">' + esc(money(p.price)) + '</span></a></li>'
      ).join('') + '</ul></div>';
    section.addEventListener('click', event => { if (event.target.closest('[data-ds-pc-recent]')) track('ds_recently_viewed_click', {source: 'product'}); });
    const anchor = document.getElementById('ds-studio-journal') || document.getElementById('questions');
    if (anchor) anchor.before(section);
  }

  function init() {
    sizeSwitcher();
    trustLine();
    upgradePrices();
    foldEngraving();
    giftStatus();
    stickyBar();
    dropHint();
    waitFor(() => document.getElementById('questions') || document.getElementById('ds-studio-journal'), () => recentlyViewed());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
